import useSWR from 'swr';
import { captureException } from '@sentry/nextjs';
import { useCurrentAccountId } from '@zapier/identity';
import { isProduction, zodParse } from '../../../utils';
import { getPaginatedRequestURL, PaginatedRequestOptions } from '../utils';
import { Account, AccountsResponse, Z_AccountsResponse } from './types';

export async function fetchAccounts(
  options: PaginatedRequestOptions = {},
): Promise<AccountsResponse> {
  const response = await fetch(
    getPaginatedRequestURL('/api/v4/accounts/', options),
  );

  const data = await response
    .json()
    .catch((error) => !isProduction() && console.error(error));

  if (!response.ok) {
    throw new Error(
      `Failed to fetch accounts: ${data?.detail ?? 'Unknown error'} (${
        response.status
      })`,
    );
  }

  zodParse(Z_AccountsResponse, data, 'AccountsResponse');

  return data;
}

export async function fetchAllAccounts(
  options?: PaginatedRequestOptions,
  previousResults: Account[] = [],
): Promise<Account[]> {
  const data = await fetchAccounts(options);

  const results = [...previousResults, ...(data.results ?? [])];

  if (data.next) {
    return fetchAllAccounts({ ...options, url: data.next }, results);
  }

  return results;
}

export function useAccounts(shouldFetch = true) {
  const currentAccountId = useCurrentAccountId();

  const { data, error, isLoading } = useSWR(
    `/api/v4/accounts/${shouldFetch}`,
    () => {
      return shouldFetch && !!currentAccountId ? fetchAllAccounts() : null;
    },
  );

  if (error) {
    !isProduction() && console.error(error);
    captureException(error);
  }

  return {
    data: data ?? null,
    error,
    isLoading,
  };
}

export function useCurrentAccount(shouldFetch = true) {
  const currentAccountId = useCurrentAccountId();
  const { data, error, isLoading } = useAccounts(shouldFetch);

  if (error) {
    !isProduction() && console.error(error);
    captureException(error);
  }

  return {
    data: data?.find(({ id }) => id === currentAccountId) ?? null,
    error,
    isLoading,
  };
}
